import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartContainer, ChartTooltip } from '../tailgrids/core/chart.js';
import { type ChartTone, SERIES_TONES, TONE_COLOR } from './chart-colors.js';
import { ChartTooltipCard, type TooltipSeries } from './ChartTooltipCard.js';

export interface BarSeries {
  key: string;
  label: string;
  tone?: ChartTone;
}

type Row = Record<string, string | number | boolean | null | undefined>;

interface BarChartProps<T extends Row> {
  data: readonly T[];
  /** The category field (x axis for columns, y axis for rows). */
  xKey: keyof T & string;
  series: readonly BarSeries[];
  /** Accessible name; the chart is announced as "<title>: <summary>". */
  title: string;
  /** One sentence a screen reader hears instead of the bars. */
  summary?: string;
  height?: number;
  stacked?: boolean;
  /** `rows` = horizontal bars with the categories down the side. */
  layout?: 'columns' | 'rows';
  /** Colours each bar of a single-series chart on its own (histogram bins, gains vs losses). */
  barTone?: (row: T) => ChartTone;
  /** Outlines the bars this returns true for (e.g. the current episode). */
  highlight?: (row: T) => boolean;
  /** Prints each bar's value beside it. */
  showValues?: boolean;
  formatValue?: (value: number) => string;
  formatCategory?: (value: string) => string;
  /** Show every n-th category label (dense histograms). */
  categoryInterval?: number;
  /** Dashed markers at a category (e.g. a histogram's average loss and average profit bins). */
  referenceLines?: readonly ChartReferenceLine[];
}

export interface ChartReferenceLine {
  /** The category the line stands on (its `xKey` value). */
  category: string;
  label: string;
  tone: ChartTone;
}

/**
 * Bars on the NextAdmin chart container (Recharts): one or more series, stacked or grouped, columns or rows, with a
 * zero line whenever a value is negative.
 */
export function BarChart<T extends Row>({
  data,
  xKey,
  series,
  title,
  summary,
  height = 260,
  stacked = false,
  layout = 'columns',
  barTone,
  highlight,
  showValues = false,
  formatValue,
  formatCategory,
  categoryInterval,
  referenceLines = [],
}: BarChartProps<T>) {
  const toned: TooltipSeries[] = series.map((s, i) => ({
    ...s,
    tone: s.tone ?? SERIES_TONES[i % SERIES_TONES.length],
  }));
  const anyNegative = data.some((row) => series.some((s) => Number(row[s.key]) < 0));
  const rows = layout === 'rows';
  const categoryAxis = {
    dataKey: (row: T) => row[xKey],
    axisLine: false,
    tickLine: false,
    interval: categoryInterval === undefined ? ('preserveEnd' as const) : categoryInterval - 1,
    tickFormatter: formatCategory,
  };
  const valueAxis = {
    axisLine: false,
    tickLine: false,
    tickFormatter: formatValue ? (value: number) => formatValue(value) : undefined,
  };

  return (
    <figure role="img" aria-label={summary ? `${title}: ${summary}` : title} className="m-0 w-full" style={{ height }}>
      <ChartContainer className="h-full w-full" width="100%" height={height}>
        <RechartsBarChart
          data={[...data]}
          layout={rows ? 'vertical' : 'horizontal'}
          margin={{
            top: referenceLines.length > 0 ? 24 : 8,
            right: showValues ? 48 : 8,
            left: rows ? 8 : -12,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={rows} horizontal={!rows} />
          {rows ? (
            <>
              <XAxis type="number" {...valueAxis} />
              <YAxis type="category" width={112} {...categoryAxis} />
            </>
          ) : (
            <>
              <XAxis type="category" dy={8} {...categoryAxis} />
              <YAxis type="number" {...valueAxis} />
            </>
          )}
          {anyNegative &&
            (rows ? (
              <ReferenceLine x={0} stroke="var(--color-faint)" />
            ) : (
              <ReferenceLine y={0} stroke="var(--color-faint)" />
            ))}
          {referenceLines.map((line) => (
            <ReferenceLine
              key={`${line.category}:${line.label}`}
              {...(rows ? { y: line.category } : { x: line.category })}
              stroke={TONE_COLOR[line.tone]}
              strokeDasharray="4 3"
              ifOverflow="extendDomain"
              label={{ value: line.label, position: rows ? 'right' : 'top', fill: TONE_COLOR[line.tone], fontSize: 11 }}
            />
          ))}
          <ChartTooltip
            cursor={{ fill: 'var(--color-surface-alt)' }}
            content={(props) => (
              <ChartTooltipCard {...props} series={toned} formatLabel={formatCategory} formatValue={formatValue} />
            )}
          />
          {toned.map((s, seriesIndex) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              stackId={stacked ? 'stack' : undefined}
              fill={TONE_COLOR[s.tone]}
              radius={rows ? [0, 4, 4, 0] : stacked && seriesIndex < toned.length - 1 ? 0 : [4, 4, 0, 0]}
              maxBarSize={28}
              isAnimationActive={false}
            >
              {(barTone || highlight) &&
                data.map((row, i) => (
                  <Cell
                    key={i}
                    fill={barTone ? TONE_COLOR[barTone(row)] : TONE_COLOR[s.tone]}
                    stroke={highlight?.(row) ? 'var(--color-heading)' : undefined}
                    strokeWidth={highlight?.(row) ? 1.5 : 0}
                  />
                ))}
              {showValues && (
                <LabelList
                  dataKey={s.key}
                  position={rows ? 'right' : 'top'}
                  className="fill-text-secondary text-xs"
                  formatter={(value) => (typeof value === 'number' && formatValue ? formatValue(value) : String(value))}
                />
              )}
            </Bar>
          ))}
        </RechartsBarChart>
      </ChartContainer>
    </figure>
  );
}
